import React, { useState, useContext } from "react";
import { FileText, UploadSimple, ArrowSquareOut, DownloadSimple, Lightning, Image } from "@phosphor-icons/react";
import axios from "axios";
import { toast } from "react-toastify";

import { toastOptions } from "../../../assets/constants";
import coverAltelis from "../../../assets/images/cover-altelis.png";
import coverLivie from "../../../assets/images/cover-livie.png";
import altelisLogo from "../../../assets/images/altelis-full.svg";
import ageelityLogo from "../../../assets/images/ageelity.png";
import { AuthContext } from "../../../contexts";
import { CustomPageHeader } from "../../../ui";

const Proposals = () => {
    const [markdown, setMarkdown] = useState("");
    const [title, setTitle] = useState("");
    const [isGenerating, setIsGenerating] = useState(false);
    const [isConverting, setIsConverting] = useState(false);
    const [docBuffer, setDocBuffer] = useState(null);
    const [isDragOver, setIsDragOver] = useState(false);
    const [fileName, setFileName] = useState(null);
    const [clientLogo, setClientLogo] = useState(null);
    const [clientLogoName, setClientLogoName] = useState(null);
    const [coverPage, setCoverPage] = useState("");
    const [brand, setBrand] = useState("altelis");
    const { token } = useContext(AuthContext);
    const fileInputRef = React.useRef(null);
    const logoInputRef = React.useRef(null);

    const handleFileLoad = (file) => {
        if (!file || !file.name.endsWith(".md")) {
            toast.error("Veuillez sélectionner un fichier Markdown (.md).", toastOptions);
            return;
        }
        const reader = new FileReader();
        reader.onload = (e) => {
            setMarkdown(e.target.result);
            setFileName(file.name);
            if (!title) setTitle(file.name.replace(".md", ""));
        };
        reader.readAsText(file);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragOver(false);
        handleFileLoad(e.dataTransfer.files[0]);
    };

    const handleLogoLoad = (file) => {
        if (!file || !file.type.startsWith("image/")) {
            toast.error("Veuillez sélectionner une image (PNG, JPG...).", toastOptions);
            return;
        }
        const reader = new FileReader();
        reader.onload = (e) => {
            setClientLogo(e.target.result);
            setClientLogoName(file.name);
        };
        reader.readAsDataURL(file);
    };

    const invalidateDoc = () => setDocBuffer(null);

    const handleGenerate = async () => {
        if (!title.trim() || !markdown.trim()) return;
        setIsGenerating(true);
        setDocBuffer(null);
        try {
            const response = await axios.post(
                `${process.env.REACT_APP_UPA_API_HOST}proposals/download`,
                { markdown, title, clientLogo, coverPage: coverPage || null, brand },
                { headers: { Authorization: "Bearer " + token }, responseType: "blob" }
            );
            setDocBuffer(response.data);
            toast.success("Document généré avec succès.", toastOptions);
        } catch (error) {
            toast.error("Erreur lors de la génération du document.", toastOptions);
        } finally {
            setIsGenerating(false);
        }
    };

    const handleDownload = () => {
        if (!docBuffer) return;
        const url = window.URL.createObjectURL(new Blob([docBuffer]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `${title}.docx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    };

    const handleUploadToDrive = async () => {
        if (!title.trim() || !markdown.trim()) return;
        setIsConverting(true);
        try {
            const response = await axios.post(
                `${process.env.REACT_APP_UPA_API_HOST}proposals/convert`,
                { markdown, title, clientLogo, coverPage: coverPage || null, brand },
                { headers: { Authorization: "Bearer " + token } }
            );
            window.open(response.data.webViewLink, "_blank");
            toast.success("Document envoyé sur Google Drive.", toastOptions);
        } catch (error) {
            toast.error("Erreur lors de l'envoi sur Google Drive.", toastOptions);
        } finally {
            setIsConverting(false);
        }
    };

    const handleReset = () => {
        setMarkdown("");
        setTitle("");
        setDocBuffer(null);
        setFileName(null);
        setClientLogo(null);
        setClientLogoName(null);
        setCoverPage("");
        setBrand("altelis");
    };

    const hasContent = markdown.trim() && title.trim();
    const isBusy = isGenerating || isConverting;

    return (
        <div className="c-app__view">
            <CustomPageHeader
                title="Propositions commerciales"
                titleIcon={{ icon: FileText, size: 30, weight: "bold" }}
                buttons={docBuffer ? [
                    {
                        label: "Enregistrer en DOCX",
                        onClick: handleDownload,
                        icon: DownloadSimple,
                        variant: "secondary",
                        disabled: isBusy,
                    },
                    {
                        label: "Envoyer sur le Drive",
                        onClick: handleUploadToDrive,
                        icon: ArrowSquareOut,
                        variant: "primary",
                        disabled: isBusy,
                        loading: isConverting,
                    },
                ] : [
                    {
                        label: "Convertir",
                        onClick: handleGenerate,
                        icon: Lightning,
                        variant: "primary",
                        disabled: !hasContent || isBusy,
                        loading: isGenerating,
                    },
                ]}
            />

            <div className="custom__modal-group">
                <label className="name">Titre du document</label>
                <input
                    type="text"
                    className="classic-input"
                    placeholder="Ex : Proposition commerciale - Livie - Client"
                    value={title}
                    onChange={(e) => { setTitle(e.target.value); invalidateDoc(); }}
                />
            </div>

            <div className="custom__modal-group">
                <label className="name">Page de garde</label>
                <div style={{ display: "flex", gap: "0.75rem" }}>
                    {[
                        { value: "", label: "Aucune", img: null },
                        { value: "altelis", label: "Altelis", img: coverAltelis },
                        { value: "livie", label: "Livie", img: coverLivie },
                    ].map(({ value, label, img }) => (
                        <div
                            key={value}
                            onClick={() => { setCoverPage(value); invalidateDoc(); }}
                            style={{
                                cursor: "pointer",
                                borderRadius: "0.625rem",
                                border: coverPage === value ? "2px solid var(--blue-500)" : "1px solid var(--black-10)",
                                padding: "0.5rem",
                                textAlign: "center",
                                transition: "all 0.2s",
                                width: 140,
                            }}
                        >
                            {img ? (
                                <img src={img} alt={label} style={{ width: "100%", height: 160, objectFit: "cover", borderRadius: "0.375rem", marginBottom: "0.375rem" }} />
                            ) : (
                                <div style={{ width: "100%", height: 160, borderRadius: "0.375rem", marginBottom: "0.375rem", backgroundColor: "var(--black-50)", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--black-60)", fontSize: "0.8125rem" }}>
                                    Aucune
                                </div>
                            )}
                            <span style={{ fontSize: "0.8125rem", fontWeight: coverPage === value ? 600 : 400, color: coverPage === value ? "var(--blue-500)" : "var(--black-60)" }}>
                                {label}
                            </span>
                        </div>
                    ))}
                </div>
            </div>

            <div className="custom__modal-group">
                <label className="name">Choix du logo entreprise</label>
                <div style={{ display: "flex", gap: "0.75rem" }}>
                    {[
                        { value: "altelis", label: "Altelis", img: altelisLogo },
                        { value: "ageelity", label: "Ageelity", img: ageelityLogo },
                    ].map(({ value, label, img }) => (
                        <div
                            key={value}
                            onClick={() => { setBrand(value); invalidateDoc(); }}
                            style={{
                                cursor: "pointer",
                                borderRadius: "0.625rem",
                                border: brand === value ? "2px solid var(--blue-500)" : "1px solid var(--black-10)",
                                padding: "0.5rem",
                                textAlign: "center",
                                transition: "all 0.2s",
                                width: 140,
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <div style={{ width: "100%", height: 60, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "0.375rem" }}>
                                <img src={img} alt={label} style={{ maxWidth: 80, maxHeight: 36, objectFit: "contain" }} />
                            </div>
                            <span style={{ fontSize: "0.8125rem", fontWeight: brand === value ? 600 : 400, color: brand === value ? "var(--blue-500)" : "var(--black-60)" }}>
                                {label}
                            </span>
                        </div>
                    ))}
                </div>
            </div>

            <div className="custom__modal-group">
                <label className="name">Logo client (optionnel)</label>
                <input ref={logoInputRef} type="file" accept="image/*" style={{ display: "none" }} onChange={(e) => { handleLogoLoad(e.target.files[0]); e.target.value = ""; }} />
                {clientLogo ? (
                    <div style={{ display: "flex", alignItems: "center", gap: "0.625rem", padding: "0.5rem 0.75rem", borderRadius: "0.625rem", border: "1px solid var(--black-10)", backgroundColor: "var(--white-100)", width: "fit-content" }}>
                        <img src={clientLogo} alt="Logo client" style={{ maxHeight: 36, maxWidth: 90, objectFit: "contain" }} />
                        <span style={{ fontSize: "0.8125rem", color: "var(--black-60)" }}>{clientLogoName}</span>
                        <button className="button-large gray-button" onClick={() => { setClientLogo(null); setClientLogoName(null); invalidateDoc(); }} style={{ padding: "0.25rem 0.5rem", minWidth: "auto" }}>✕</button>
                    </div>
                ) : (
                    <button className="button-large gray-button" onClick={() => logoInputRef.current?.click()} style={{ width: "fit-content" }}>
                        <Image size={16} weight="bold" />
                        <span>Choisir un logo</span>
                    </button>
                )}
            </div>

            <div className="custom__modal-group">
                <label className="name">Fichier Markdown</label>
                <div
                    className={`csv-importer${isDragOver ? " csv-importer--drag-over" : ""}${fileName ? " csv-importer--loaded" : ""}`}
                    onDrop={handleDrop}
                    onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
                    onDragLeave={() => setIsDragOver(false)}
                    onClick={() => !fileName && fileInputRef.current?.click()}
                    style={{ cursor: fileName ? "default" : "pointer" }}
                >
                    <input ref={fileInputRef} type="file" accept=".md" className="csv-importer__input" onChange={(e) => { handleFileLoad(e.target.files[0]); e.target.value = ""; }} />
                    {fileName ? (
                        <div className="csv-importer__file">
                            <span className="csv-importer__filename">{fileName}</span>
                            <button className="csv-importer__reset" onClick={(e) => { e.stopPropagation(); handleReset(); }}>✕</button>
                        </div>
                    ) : (
                        <div className="csv-importer__placeholder">
                            <div className="csv-importer__placeholder-icon"><UploadSimple size={24} weight="bold" /></div>
                            <span className="csv-importer__placeholder-label">Importer un fichier Markdown</span>
                            <span className="csv-importer__hint">Glisser-déposer ou cliquer</span>
                        </div>
                    )}
                </div>
            </div>

            {fileName && (
                <div className="custom__modal-group">
                    <label className="comments">Éditer le contenu ({markdown.split("\n").length} lignes)</label>
                    <textarea
                        className="classic-input classic-input--textarea"
                        value={markdown}
                        onChange={(e) => { setMarkdown(e.target.value); invalidateDoc(); }}
                        rows={12}
                        style={{ fontFamily: "monospace", resize: "vertical", minHeight: "12rem", height: "auto" }}
                    />
                </div>
            )}
        </div>
    );
};

export default Proposals;
